import moment from "./moment-round";
import type { TicketData } from "./types";

function random(min: number, max: number): number {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

export class Ticket implements TicketData {
  animation: string = "pulse";
  cena: number = 0;
  id: string = "";
  kodZabezpieczający: number | string = "";
  label: string = "";
  liczbaMinut: number = 0;
  miasto: string = "";
  nazwa: string = "";
  numerBiletu: string = "";
  numerBoczny: string = "";
  pokazCzasSkasowania: boolean = true;
  pokazCzasSkasowaniaTimer: boolean = true;
  pokazCzasZakupu: boolean = true;
  pokazKodZabezpieczajacy: boolean = true;
  pokazNumerBoczny: boolean = true;
  pokazStrefe: boolean = false;
  pokazZnizke: boolean = false;

  duration: moment.Duration;
  valid_start_date: moment.Moment;
  valid_end_date: moment.Moment;
  purchase_date: moment.Moment;

  [key: string]: unknown;

  constructor(object: TicketData) {
    Object.assign(this, object);

    const savedNumerBoczny = localStorage.getItem("numerBoczny");
    if (savedNumerBoczny !== null && savedNumerBoczny !== "") {
      this.numerBoczny = savedNumerBoczny;
    }

    const savedKodZabezpieczajacy = localStorage.getItem("kodZabezpieczający");
    if (savedKodZabezpieczajacy !== null && savedKodZabezpieczajacy !== "") {
      this.kodZabezpieczający = savedKodZabezpieczajacy;
    }

    // Wczytywanie dynamicznie zmodyfikowanych pól <span>
    const savedSpansJson = localStorage.getItem("savedSpanValues");
    if (savedSpansJson) {
      try {
        const savedSpans = JSON.parse(savedSpansJson);
        for (const key in savedSpans) {
          if (this[key] !== undefined) {
            this[key] = savedSpans[key];
          }
        }
      } catch (e) {
        console.error("Błąd podczas parsowania savedSpanValues z localStorage:", e);
      }
    }

    this.duration = moment.duration(this.liczbaMinut, "minutes");
    this.valid_start_date = this.setValidStartDate();
    this.valid_end_date = this.setValidEndDate();
    this.purchase_date = this.setPurchaseDate();

    this.updateDOM();
  }

  static async create(city: string, ticket: string): Promise<Ticket> {
    const filePath = `/cities/${city}/${ticket}.json`;
    const response = await fetch(filePath);
    const data: TicketData = await response.json();

    return new Ticket(data);
  }

  consoleLog(): void {
    console.log(this);
    console.log(this.timeLeft);
  }

  changeSideNumberWithPrompt(): void {
    let newValue = prompt("Podaj numer boczny:", this.numerBoczny);

    if (newValue === null || newValue === "") {
      newValue = this.numerBoczny;
    } else {
      this.numerBoczny = newValue.toString();
      localStorage.setItem("numerBoczny", this.numerBoczny);
    }
    this.updateValue("numerBoczny");
  }

  setSideNumber(value: string): void {
    if (value) {
      this.numerBoczny = value.toString();
      localStorage.setItem("numerBoczny", this.numerBoczny);
      this.updateValue("numerBoczny");
    }
  }

  changeSecurityCode(): void {
    let newValue = prompt("Podaj numer kontrolny:", this.kodZabezpieczający.toString());
    if (newValue === null || newValue === "") {
      newValue = this.kodZabezpieczający.toString();
    } else {
      this.kodZabezpieczający = newValue.toString();
      localStorage.setItem("kodZabezpieczający", this.kodZabezpieczający.toString());
    }
    this.updateValue("kodZabezpieczający");
  }

  updateValue(property: string, value: unknown = null): void {
    const valToSet = value ?? this[property];
    const domObject = document.getElementById(property);
    if (domObject !== null && valToSet !== undefined && valToSet !== null) {
      domObject.innerText = String(valToSet);
    }
  }

  updateDOM(): void {
    const savedNumerBoczny = localStorage.getItem("numerBoczny");
    if (savedNumerBoczny !== null && savedNumerBoczny !== "") {
      this.numerBoczny = savedNumerBoczny;
    }

    for (const property in this) {
      const domObject = document.getElementById(property);
      if (domObject !== null) {
        // Sprawdzamy czy dany element nie posiada zapisanego custom wariantu w localStorage dla pola span
        const savedSpanField = localStorage.getItem(`span_field_${property}`);
        if (savedSpanField !== null) {
          domObject.innerText = savedSpanField;
          continue;
        }

        const val = this[property];
        if (moment.isMoment(val)) {
          domObject.innerText = this.getDate(property);
        } else if (property === "cena" && typeof val === "number") {
          domObject.innerText = val.toFixed(2).replace(".", ",");
        } else if (val !== undefined && val !== null) {
          domObject.innerText = String(val);
        }
      }
    }

    // Przywracanie zapisanych pól <span> również dla tych bez bezpośrednich właściwości klasy Ticket (np. z kwasną klasą lub bez ID)
    document.querySelectorAll("span").forEach((el) => {
      const spanEl = el as HTMLElement;
      if (spanEl.id) {
        const savedVal = localStorage.getItem(`span_field_${spanEl.id}`);
        if (savedVal !== null) {
          spanEl.innerText = savedVal;
        }
      } else if (spanEl.className) {
        const key = `span_class_${spanEl.className.replace(/\s+/g, "_")}`;
        const savedVal = localStorage.getItem(key);
        if (savedVal !== null) {
          spanEl.innerText = savedVal;
        }
      }
    });

    const savedQrImage = localStorage.getItem("selected_qr_image");
    if (savedQrImage) {
      document.querySelectorAll(".qr_code img").forEach((el) => {
        (el as HTMLImageElement).src = savedQrImage;
      });
    }

    const cityPrefix = this.id.split("-")[0] || "warszawa";

    document.querySelectorAll(".mzk_city").forEach((el) => {
      (el as HTMLElement).innerText = this.miasto;
    });
    document.querySelectorAll(".mzk_icon").forEach((el) => {
      (el as HTMLImageElement).src = `/cities/${cityPrefix}/icon.png`;
    });
    document.querySelectorAll(".mzk_animated_logo").forEach((el) => {
      (el as HTMLImageElement).src = `/cities/${cityPrefix}/animated.png`;
    });
    document.querySelectorAll(".mzk_animated_logo").forEach((el) => {
      (el as HTMLElement).className = `mzk_animated_logo ${this.animation}`;
    });

    const toggleClass = (selector: string, show: boolean) => {
      document.querySelectorAll(selector).forEach((el) => {
        if (show) {
          el.classList.remove("hide");
        } else {
          el.classList.add("hide");
        }
      });
    };

    toggleClass(".ticket_valid_start_date", this.pokazCzasSkasowania);
    toggleClass(".ticket_time_from_start", this.pokazCzasSkasowaniaTimer);
    toggleClass(".ticket_purchase_date", this.pokazCzasZakupu);
    
    toggleClass(".security_code", this.pokazKodZabezpieczajacy);
    toggleClass(".side_number", this.pokazNumerBoczny);
    toggleClass(".zone", this.pokazStrefe);
    toggleClass(".discount", this.pokazZnizke);
  }

  setValidStartDate(): moment.Moment {
    const duration = this.duration;
    const purchaseTime = moment.duration(duration).subtract(random(420, 460), "seconds");
    const newDate = moment().subtract(purchaseTime.asMilliseconds() / 2, "milliseconds");
    this.valid_start_date = newDate;
    return newDate;
  }

  setValidEndDate(): moment.Moment {
    const startDate = moment(this.valid_start_date);
    const newDate = startDate.add(this.duration);
    this.valid_end_date = newDate;
    return newDate;
  }

  getDate(selected_date: string): string {
    const val = this[selected_date];
    if (moment.isMoment(val)) {
      return val.format("YYYY-MM-DD HH:mm:ss");
    }
    return "";
  }

  setPurchaseDate(): moment.Moment {
    const startDate = moment(this.valid_start_date);
    const newDate = startDate.subtract(random(611, 655), "seconds");
    this.purchase_date = newDate;
    return newDate;
  }

  get timeLeft(): moment.Duration {
    return moment.duration(moment(this.valid_end_date).diff(moment()));
  }

  printTimeLeft(): void {
    const timeLeft = this.timeLeft;
    const hours = Math.floor(timeLeft.asHours());
    const minutes = timeLeft.minutes();
    const seconds = timeLeft.seconds();

    const formattedTime = `${hours}:${minutes.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`;
    const timeToEndEl = document.getElementById("time_to_end");
    if (timeToEndEl) {
      timeToEndEl.innerText = formattedTime;
    }

    const percentage = Math.round((timeLeft.asMilliseconds() / this.duration.asMilliseconds()) * 100);

    const meterEl = document.querySelector(".ticket_time_to_end .countdown_meter .meter") as HTMLElement | null;
    if (meterEl) {
      meterEl.style.width = `${Math.max(0, Math.min(100, percentage))}%`;
    }
  }
}
